
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AiReportMarkdown from './AiReportMarkdown';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { fetchRatioTrend } from '../../../services/analysisRatioTrend';
import {
  fetchAiProviders,
  fetchAiTenantAdminOptions,
  fetchSavedAiFinancialReport,
  fetchSavedAiFinancialReports,
  generateAiFinancialReport
} from '../../../services/analysisAiReport';
import { fetchSectorRatioAnalysis } from '../../../services/analysisSectorRatio';
import { fetchStandardRatios } from '../../../services/analysisRatiosStandard';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { exportAiReportPdf } from './aiReportPdfExport';
import { exportAiReportExcel } from './aiReportExcelExport';
import './AiFinancialReportPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const UI_TEXT = {
  'tr-TR': {
    noData: 'Veri yok.',
    noTrendData: 'Trend verisi yok.',
    trendChartAria: 'Rasyo trend grafigi',
    title: 'AI Mali Analiz ve Karar Destek Paneli',
    description: 'Executive Snapshot + Drivers + Actions hiyerarsisi ile sirketin genel sagligini ve kritik risklerini sunar.',
    analysisLoading: 'Analiz Yukleniyor...',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    exportingExcel: 'Excel olusturuluyor...',
    exportExcel: 'Excel Al',
    refreshAnalysis: 'Analizi Yenile',
    generating: 'Olusturuluyor...',
    createAiReport: 'AI Raporu Olustur',
    reportingBasis: 'Raporlama Bazi',
    ifrsDefault: 'IFRS (Varsayilan)',
    vukOption: 'VUK',
    activeBasis: 'Analiz Bazi',
    basisMismatchNotice: 'Sektor karsilastirmasi IFRS sirket verisini VUK/EVDS benchmark ile eslestirir.',
    tenantUser: 'Kullanici (Tenant)',
    allTenantAdmins: 'Tum tenant adminlari',
    loading: 'Yukleniyor...',
    tenantAdminMissing: 'Tenant admin bulunamadi',
    language: 'Dil',
    turkish: 'Turkce',
    english: 'English',
    trendStartPeriod: 'Trend Baslangic Donemi',
    trendEndPeriod: 'Trend Bitis Donemi',
    trendMaxPeriod: 'Trend Maksimum Donem',
    temperature: 'Sicaklik (temperature)',
    aiProvider: 'AI Saglayici',
    providerAuto: 'Otomatik (yapılandırılmış ilk)',
    providerNotConfigured: '(yapılandırılmamış)',
    selectCompanyPeriod: 'Lutfen sirket ve donem secin.',
    reportSaved: 'Rapor kaydedildi:',
    reportGenerateError: 'AI mali analiz raporu olusturulamadi.',
    reportLoaded: 'Kayitli rapor yuklendi:',
    reportLoadError: 'Kayitli AI raporu yuklenemedi.',
    analysisLoadError: 'Analiz verisi yuklenemedi.',
    score: 'Finansal Saglik',
    aboveStandard: 'Standart Ustu Rasyo',
    alarmRatio: 'Alarm Veren Rasyo',
    aboveSector: 'Sektor Uzeri',
    noBenchmark: 'benchmark yok',
    overallScore: 'Genel Durum Skoru',
    executiveSummary: 'Yonetici Ozeti',
    strong: 'Guclu',
    risk: 'Risk',
    action: 'Aksiyon',
    riskAlarm: 'Risk Alarmi: Finansal Kaldirac',
    debtToEquity: 'Borc/Ozkaynak',
    standard: 'standart',
    whatIf: 'What-if sim: Borc/Ozkaynak',
    simulatedValue: 'Simule deger',
    scoreImpact: 'Skor etkisi',
    newRisk: 'Yeni risk',
    ratioTrend: 'Rasyo Trend Analizi',
    ratio: 'Rasyo',
    std: 'Std',
    diff: 'Fark',
    sectorBenchmark: 'Sektorel Benchmark (Bullet)',
    drillDown: 'Drill-down',
    noSectorData: 'Sektor benchmark verisi yok.',
    company: 'Sirket',
    sector: 'Sektor',
    criticalDeviation: 'En Kritik Sapmalar (Diverging)',
    cap: 'Cap: +/-250%',
    noDeviationData: 'Sapma verisi bulunmuyor.',
    strengthWeakness: 'Strength vs Weakness',
    strengths: 'Guclu Yonler',
    weaknesses: 'Zayif Yonler',
    formulaHint: 'Hesaplama Dayanagi',
    undefined: 'Tanimsiz',
    highestImpactRatios: 'En Yuksek Etkili Rasyolar',
    sort: 'Siralama',
    sortAbs: 'Sapma (Mutlak)',
    sortRisk: 'Risk Once',
    sortPositive: 'Pozitif Sapma',
    status: 'Durum',
    noRatioData: 'Rasyo verisi yok.',
    actionCards: 'Aksiyon Kartlari',
    debtProgram: 'Borc Yonetimi Programi',
    package: 'Iyilestirme Paketi',
    impactArea: 'Etki Alani',
    efficiency: 'Verimlilik',
    expectedImpact: 'Beklenen Etki',
    cashFlowProj: 'Nakit akis projeksiyonu guncelle',
    monthlyKpi: 'Aylik hedef KPI siniri belirle',
    ownerDueDate: 'Sorumlu ekip ve tarih ata',
    addToPlan: "Plan'a Ekle",
    dataQualityIssues: 'Data Quality Issues',
    dataQualityNotFound: 'Data quality problemi tespit edilmedi.',
    issueType: 'Issue Type',
    dataQualityIssue: 'Data Quality Issue',
    note: 'Not',
    invalidValue: 'Deger eksik veya anlamsiz.',
    savedReports: 'Kaydedilen Raporlar',
    recordCount: 'kayit',
    reportName: 'Rapor Adi',
    dateTime: 'Tarih-Saat',
    noSavedReport: 'Kayitli rapor yok.',
    load: 'Yukle',
    savedName: 'Kayit Adi',
    source: 'Kaynak',
    latency: 'Gecikme',
    aiReportText: 'AI Rapor Metni (Izlenebilir Kayit)',
    promptTokens: 'Prompt Tokens',
    completionTokens: 'Completion Tokens',
    totalTokens: 'Total Tokens',
    totalCostUsd: 'Toplam Maliyet (USD)',
    inputCostUsd: 'Input Maliyeti (USD)',
    outputCostUsd: 'Output Maliyeti (USD)',
    ratioCol: 'Rasyo',
    difference: 'Fark',
    positive: 'Positive',
    performance: 'Performance',
    dataQuality: 'Data Quality',
    aiVerdict: 'AI Verdict',
    coverage: 'Veri Kapsami',
    warnings: 'Uyarilar',
    unknown: 'Unknown',
    med: 'Med',
    low: 'Low',
    high: 'High',
    reasonStrong: 'En guclu taraf',
    reasonRisk: 'En kritik risk',
    summaryStrongNoData: 'En guclu taraf icin yeterli veri yok.',
    summaryNoRisk: 'Kritik risk bulunmuyor.',
    topAction: '1 numarali aksiyon',
    debtReductionPlan: 'borc azaltma plani',
    operationalPlan: 'operasyonel verimlilik plani',
    loadDataFirst: 'Aksiyon icin once verileri yukleyin.',
    leverage: 'leverage'
  },
  'en-US': {
    noData: 'No data.',
    noTrendData: 'No trend data.',
    trendChartAria: 'Ratio trend chart',
    title: 'AI Financial Analysis and Decision Support Panel',
    description: 'Provides the company health and critical risks with an Executive Snapshot + Drivers + Actions hierarchy.',
    analysisLoading: 'Loading Analysis...',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    exportingExcel: 'Building Excel...',
    exportExcel: 'Export Excel',
    refreshAnalysis: 'Refresh Analysis',
    generating: 'Generating...',
    createAiReport: 'Generate AI Report',
    reportingBasis: 'Reporting Basis',
    ifrsDefault: 'IFRS (Default)',
    vukOption: 'VUK',
    activeBasis: 'Analysis Basis',
    basisMismatchNotice: 'Sector comparison matches IFRS company data with VUK/EVDS benchmark.',
    tenantUser: 'User (Tenant)',
    allTenantAdmins: 'All tenant admins',
    loading: 'Loading...',
    tenantAdminMissing: 'Tenant admin not found',
    language: 'Language',
    turkish: 'Turkish',
    english: 'English',
    trendStartPeriod: 'Trend Start Period',
    trendEndPeriod: 'Trend End Period',
    trendMaxPeriod: 'Trend Max Period',
    temperature: 'Temperature',
    aiProvider: 'AI Provider',
    providerAuto: 'Auto (first configured)',
    providerNotConfigured: '(not configured)',
    selectCompanyPeriod: 'Please select company and period.',
    reportSaved: 'Report saved:',
    reportGenerateError: 'AI financial analysis report could not be generated.',
    reportLoaded: 'Saved report loaded:',
    reportLoadError: 'Saved AI report could not be loaded.',
    analysisLoadError: 'Analysis data could not be loaded.',
    score: 'Financial Health',
    aboveStandard: 'Ratios Above Standard',
    alarmRatio: 'Alerting Ratios',
    aboveSector: 'Above Sector',
    noBenchmark: 'no benchmark',
    overallScore: 'Overall Score',
    executiveSummary: 'Executive Summary',
    strong: 'Strong',
    risk: 'Risk',
    action: 'Action',
    riskAlarm: 'Risk Alarm: Financial Leverage',
    debtToEquity: 'Debt/Equity',
    standard: 'standard',
    whatIf: 'What-if sim: Debt/Equity',
    simulatedValue: 'Simulated value',
    scoreImpact: 'Score impact',
    newRisk: 'New risk',
    ratioTrend: 'Ratio Trend Analysis',
    ratio: 'Ratio',
    std: 'Std',
    diff: 'Diff',
    sectorBenchmark: 'Sector Benchmark (Bullet)',
    drillDown: 'Drill-down',
    noSectorData: 'No sector benchmark data.',
    company: 'Company',
    sector: 'Sector',
    criticalDeviation: 'Critical Deviations (Diverging)',
    cap: 'Cap: +/-250%',
    noDeviationData: 'No deviation data.',
    strengthWeakness: 'Strength vs Weakness',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    formulaHint: 'Calculation Basis',
    undefined: 'Undefined',
    highestImpactRatios: 'Highest Impact Ratios',
    sort: 'Sort',
    sortAbs: 'Deviation (Absolute)',
    sortRisk: 'Risk First',
    sortPositive: 'Positive Deviation',
    status: 'Status',
    noRatioData: 'No ratio data.',
    actionCards: 'Action Cards',
    debtProgram: 'Debt Management Program',
    package: 'Improvement Package',
    impactArea: 'Impact Area',
    efficiency: 'Efficiency',
    expectedImpact: 'Expected Impact',
    cashFlowProj: 'Update cash flow projection',
    monthlyKpi: 'Set monthly KPI target boundary',
    ownerDueDate: 'Assign owner team and due date',
    addToPlan: 'Add to Plan',
    dataQualityIssues: 'Data Quality Issues',
    dataQualityNotFound: 'No data quality issues detected.',
    issueType: 'Issue Type',
    dataQualityIssue: 'Data Quality Issue',
    note: 'Note',
    invalidValue: 'Missing or invalid value.',
    savedReports: 'Saved Reports',
    recordCount: 'records',
    reportName: 'Report Name',
    dateTime: 'Date-Time',
    noSavedReport: 'No saved report.',
    load: 'Load',
    savedName: 'Saved Name',
    source: 'Source',
    latency: 'Latency',
    aiReportText: 'AI Report Text (Traceable Record)',
    promptTokens: 'Prompt Tokens',
    completionTokens: 'Completion Tokens',
    totalTokens: 'Total Tokens',
    totalCostUsd: 'Total Cost (USD)',
    inputCostUsd: 'Input Cost (USD)',
    outputCostUsd: 'Output Cost (USD)',
    ratioCol: 'Ratio',
    difference: 'Difference',
    positive: 'Positive',
    performance: 'Performance',
    dataQuality: 'Data Quality',
    aiVerdict: 'AI Verdict',
    coverage: 'Coverage',
    warnings: 'Warnings',
    unknown: 'Unknown',
    med: 'Med',
    low: 'Low',
    high: 'High',
    reasonStrong: 'Strongest side',
    reasonRisk: 'Most critical risk',
    summaryStrongNoData: 'Not enough data for strongest side.',
    summaryNoRisk: 'No critical risk found.',
    topAction: 'Top action',
    debtReductionPlan: 'debt reduction plan',
    operationalPlan: 'operational efficiency plan',
    loadDataFirst: 'Load data first for action planning.',
    leverage: 'leverage'
  }
};

const RATIO_TITLE_TRANSLATIONS = {
  debt_to_equity_ratio: 'Debt to Equity Ratio',
  return_on_equity: 'Return on Equity (ROE)',
  return_on_assets: 'Return on Assets (ROA)',
  net_profit_margin: 'Net Profit Margin'
};
const PROVIDER_LABELS = {
  elysium: 'Elysium AI Gateway',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  n8n: 'n8n'
};
const PROVIDER_ORDER = ['elysium', 'openai', 'anthropic', 'n8n'];

const formatProviderName = (value) => {
  const key = (value || '').toLowerCase();
  if (!key) {
    return '-';
  }
  return PROVIDER_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

const sortProviders = (items = []) => [...items].sort((a, b) => {
  const aName = (a.providerName || '').toLowerCase();
  const bName = (b.providerName || '').toLowerCase();
  const aIndex = PROVIDER_ORDER.includes(aName) ? PROVIDER_ORDER.indexOf(aName) : PROVIDER_ORDER.length;
  const bIndex = PROVIDER_ORDER.includes(bName) ? PROVIDER_ORDER.indexOf(bName) : PROVIDER_ORDER.length;
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  if (a.configured !== b.configured) {
    return a.configured ? -1 : 1;
  }
  return aName.localeCompare(bName);
});

const humanizeRatioKey = (key) => (key || '')
  .split('_')
  .filter(Boolean)
  .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
  .join(' ');

const formatUsd = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
const formatNum = (value, locale = 'tr-TR', digits = 2) => {
  const n = Number(value);
  return Number.isNaN(n) ? '-' : n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const formatSigned = (value, locale = 'tr-TR', digits = 1) => {
  const n = Number(value);
  if (Number.isNaN(n)) {
    return '-';
  }
  return `${n > 0 ? '+' : ''}${n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};
const formatPercent = (value, locale = 'tr-TR', digits = 1) => {
  const n = Number(value);
  return Number.isNaN(n) ? '-' : `${n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};
const formatDateTime = (value, locale = 'tr-TR') => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(locale) : '-';
};
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const getPeriodLabel = (period, locale = 'tr-TR') => {
  const fallback = locale.startsWith('en') ? 'Period' : 'Donem';
  return period?.code || period?.description || `${fallback} ${period?.id ?? ''}`;
};
const normalizeRoleName = (value) => {
  const raw = (value || '').trim();
  if (!raw) {
    return '';
  }
  const canonical = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (canonical === 'SYSADMIN') {
    return 'SYS_ADMIN';
  }
  if (canonical === 'TENANTADMIN') {
    return 'TENANT_ADMIN';
  }
  if (canonical === 'TENANTUSER') {
    return 'TENANT_USER';
  }
  return raw.toUpperCase();
};

const resolveRoleFromStorage = () => {
  const storedRole = normalizeRoleName(sessionStorage.getItem('authRole'));
  if (storedRole) {
    return storedRole;
  }
  const token = sessionStorage.getItem('authToken');
  if (!token || !token.includes('.')) {
    return '';
  }
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return normalizeRoleName(decoded?.role);
  } catch (_error) {
    return '';
  }
};

const riskKeys = new Set(['debt_to_equity_ratio', 'debt_ratio']);
const fallbackFormula = {
  'tr-TR': {
    debt_to_equity_ratio: 'Toplam Borc / Ozkaynak',
    return_on_equity: 'Net Kar / Ozkaynak',
    return_on_assets: 'Net Kar / Toplam Aktif',
    net_profit_margin: 'Net Kar / Satislar'
  },
  'en-US': {
    debt_to_equity_ratio: 'Total Debt / Equity',
    return_on_equity: 'Net Income / Equity',
    return_on_assets: 'Net Income / Total Assets',
    net_profit_margin: 'Net Income / Sales'
  }
};

const deviationPct = (value, standard) => {
  const v = Number(value);
  const s = Number(standard);
  if (Number.isNaN(v) || Number.isNaN(s) || Math.abs(s) < 0.000001) {
    return null;
  }
  return ((v - s) / Math.abs(s)) * 100;
};

const Sparkline = ({ values }) => {
  const valid = values.filter((v) => Number.isFinite(v));
  if (!valid.length) {
    return <div className="sparkline-empty">-</div>;
  }
  const w = 132;
  const h = 34;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = Math.max(max - min, 0.000001);
  const pts = valid.map((value, index) => {
    const x = valid.length === 1 ? w / 2 : (index / (valid.length - 1)) * w;
    const y = h - ((value - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return <svg viewBox={`0 0 ${w} ${h}`} className="sparkline"><polyline points={pts} /></svg>;
};

const TrendChart = ({ points, standard, t, language }) => {
  if (!points.length) {
    return <p className="chart-empty">{t('noTrendData')}</p>;
  }
  const w = 760;
  const h = 220;
  const lp = 38;
  const rp = 20;
  const tp = 14;
  const bp = 34;
  const gw = w - lp - rp;
  const gh = h - tp - bp;
  const values = points.map((item) => item.value);
  const all = Number.isFinite(Number(standard)) ? [...values, Number(standard)] : values;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = Math.max(max - min, 0.000001);
  const xOf = (index) => lp + (points.length === 1 ? gw / 2 : (index / (points.length - 1)) * gw);
  const yOf = (value) => tp + ((max - value) / range) * gh;
  const line = points.map((item, index) => `${xOf(index)},${yOf(item.value)}`).join(' ');
  const standardY = Number.isFinite(Number(standard)) ? yOf(Number(standard)) : null;

  return (
    <div className="trend-plot-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} className="trend-plot" role="img" aria-label={t('trendChartAria')}>
        <line x1={lp} y1={tp} x2={lp} y2={tp + gh} className="plot-axis" />
        <line x1={lp} y1={tp + gh} x2={lp + gw} y2={tp + gh} className="plot-axis" />
        {standardY !== null && <line x1={lp} y1={standardY} x2={lp + gw} y2={standardY} className="plot-ref" />}
        <polyline points={line} className="plot-line" />
        {points.map((item, index) => (
          <g key={`${item.periodCode}-${index}`}>
            <circle cx={xOf(index)} cy={yOf(item.value)} r="3.8" className="plot-point" />
            <title>{`${item.periodCode} | ${t('company')} ${formatNum(item.value, language, 4)} | ${t('standard')} ${formatNum(standard, language, 4)} | ${t('diff')} ${formatPercent(item.diffPct, language, 2)}`}</title>
          </g>
        ))}
      </svg>
    </div>
  );
};

function AiFinancialReportPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [temperature, setTemperature] = useState(0.3);
  const [provider, setProvider] = useState('');
  const [providers, setProviders] = useState([]);
  const [basis, setBasis] = useState('IFRS');
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [message, setMessage] = useState('');
  const pageCaptureRef = useRef(null);
  const providerInitializedRef = useRef(false);
  const [tenantAdminOptions, setTenantAdminOptions] = useState([]);
  const [selectedTenantAdminUserId, setSelectedTenantAdminUserId] = useState('');
  const [loadingTenantAdmins, setLoadingTenantAdmins] = useState(false);

  const [report, setReport] = useState(null);
  const [analysisCoverage, setAnalysisCoverage] = useState([]);
  const [analysisWarnings, setAnalysisWarnings] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [orderedPeriods, setOrderedPeriods] = useState([]);
  const [standardData, setStandardData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [sectorData, setSectorData] = useState(null);
  const [trendStartPeriodId, setTrendStartPeriodId] = useState('');
  const [trendEndPeriodId, setTrendEndPeriodId] = useState('');
  const [trendMaxPeriods, setTrendMaxPeriods] = useState(12);

  const [trendKey, setTrendKey] = useState('');
  const [sortMode, setSortMode] = useState('abs');
  const [debtScenario, setDebtScenario] = useState(null);
  const t = useCallback((key) => UI_TEXT[language]?.[key] || UI_TEXT['tr-TR'][key] || key, [language]);
  const translateRatioTitle = useCallback((item) => {
    if (!item) {
      return '';
    }
    if (!language.startsWith('en')) {
      return item.title;
    }
    return RATIO_TITLE_TRANSLATIONS[item.key] || humanizeRatioKey(item.key) || item.title;
  }, [language]);
  const authRole = resolveRoleFromStorage();
  const isSysAdmin = authRole === 'SYS_ADMIN';

  const hasSelection = Boolean(selection.companyId && selection.periodId);
  const hasTrendSelection = Boolean(trendStartPeriodId && trendEndPeriodId);
  const providerOptions = useMemo(() => sortProviders(providers), [providers]);

  useEffect(() => {
    fetchAiProviders().then(setProviders).catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    if (providerInitializedRef.current || providers.length === 0) {
      return;
    }
    providerInitializedRef.current = true;
    const elysium = providers.find((item) => item.providerName?.toLowerCase() === 'elysium' && item.configured);
    if (elysium) {
      setProvider(elysium.providerName);
    }
  }, [providers]);

  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/periods`, { headers: buildHeaders() });
        if (!response.ok) {
          throw new Error();
        }
        const periods = await response.json();
        setOrderedPeriods([...(periods || [])].sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0)));
      } catch {
        setOrderedPeriods([]);
      }
    };
    loadPeriods();
  }, []);

  useEffect(() => {
    const loadTenantAdmins = async () => {
      setLoadingTenantAdmins(true);
      try {
        const options = await fetchAiTenantAdminOptions();
        const normalized = (options || []).map((item) => ({
          value: String(item.userId),
          label: `${item.username || '-'} (${item.fullName || '-'})`
        }));
        setTenantAdminOptions(normalized);
        setSelectedTenantAdminUserId((prev) => {
          if (isSysAdmin) {
            if (prev === '' || normalized.some((item) => item.value === prev)) {
              return prev;
            }
            return '';
          }
          if (prev && normalized.some((item) => item.value === prev)) {
            return prev;
          }
          return normalized[0]?.value || '';
        });
      } catch {
        setTenantAdminOptions([]);
        setSelectedTenantAdminUserId('');
      } finally {
        setLoadingTenantAdmins(false);
      }
    };
    loadTenantAdmins();
  }, [isSysAdmin]);

  useEffect(() => {
    if (!orderedPeriods.length) {
      setTrendStartPeriodId('');
      setTrendEndPeriodId('');
      return;
    }

    const endIndexFromSelection = orderedPeriods.findIndex((period) => String(period.id) === String(selection.periodId));
    const endIndex = endIndexFromSelection >= 0 ? endIndexFromSelection : orderedPeriods.length - 1;
    const startIndex = Math.max(0, endIndex - 5);

    setTrendStartPeriodId(String(orderedPeriods[startIndex].id));
    setTrendEndPeriodId(String(orderedPeriods[endIndex].id));
    setTrendMaxPeriods(endIndex - startIndex + 1);
  }, [orderedPeriods, selection.periodId]);

  const selectedTrendStartDate = useMemo(
    () => orderedPeriods.find((period) => String(period.id) === trendStartPeriodId)?.startDate,
    [orderedPeriods, trendStartPeriodId]
  );

  const selectedTrendEndDate = useMemo(
    () => orderedPeriods.find((period) => String(period.id) === trendEndPeriodId)?.startDate,
    [orderedPeriods, trendEndPeriodId]
  );

  const filteredTrendStartPeriods = useMemo(() => {
    if (!selectedTrendEndDate) {
      return orderedPeriods;
    }
    const endTime = new Date(selectedTrendEndDate).getTime();
    if (Number.isNaN(endTime)) {
      return orderedPeriods;
    }
    return orderedPeriods.filter((period) => {
      const periodTime = new Date(period.startDate || '').getTime();
      return Number.isNaN(periodTime) ? true : periodTime <= endTime;
    });
  }, [orderedPeriods, selectedTrendEndDate]);

  const filteredTrendEndPeriods = useMemo(() => {
    if (!selectedTrendStartDate) {
      return orderedPeriods;
    }
    const startTime = new Date(selectedTrendStartDate).getTime();
    if (Number.isNaN(startTime)) {
      return orderedPeriods;
    }
    return orderedPeriods.filter((period) => {
      const periodTime = new Date(period.startDate || '').getTime();
      return Number.isNaN(periodTime) ? true : periodTime >= startTime;
    });
  }, [orderedPeriods, selectedTrendStartDate]);

  const loadAnalysis = useCallback(async (companyId, periodId) => {
    if (!companyId || !periodId) {
      return;
    }
    setLoadingAnalysis(true);
    try {
      const [stdResult, trnResult, secResult] = await Promise.allSettled([
        fetchStandardRatios(companyId, periodId, basis),
        hasTrendSelection
          ? fetchRatioTrend(companyId, trendStartPeriodId, trendEndPeriodId, trendMaxPeriods, basis)
          : Promise.resolve(null),
        fetchSectorRatioAnalysis(companyId, periodId, undefined, undefined, basis)
      ]);
      if (stdResult.status !== 'fulfilled') {
        throw stdResult.reason;
      }
      const std = stdResult.value;
      const trn = trnResult.status === 'fulfilled' ? trnResult.value : null;
      const sec = secResult.status === 'fulfilled' ? secResult.value : null;
      const coverage = [
        { key: 'standardRatios', status: 'READY', detail: 'OK' },
        { key: 'trend', status: trnResult.status === 'fulfilled' ? 'READY' : 'UNAVAILABLE', detail: trnResult.status === 'fulfilled' ? 'OK' : (trnResult.reason?.message || t('noTrendData')) },
        { key: 'sector', status: secResult.status === 'fulfilled' ? 'READY' : 'UNAVAILABLE', detail: secResult.status === 'fulfilled' ? 'OK' : (secResult.reason?.message || t('noSectorData')) }
      ];
      const warnings = coverage.filter((item) => item.status !== 'READY').map((item) => `${item.key}: ${item.detail}`);
      setAnalysisCoverage(coverage);
      setAnalysisWarnings(warnings);
      setStandardData(std);
      setTrendData(trn);
      setSectorData(sec);
      setTrendKey((prev) => prev || std?.items?.[0]?.key || '');
      const debt = (std?.items || []).find((item) => item.key === 'debt_to_equity_ratio');
      setDebtScenario(Number(debt?.calculatedValue || 0));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('analysisLoadError'));
      setAnalysisCoverage([]);
      setAnalysisWarnings([]);
      setStandardData(null);
      setTrendData(null);
      setSectorData(null);
    } finally {
      setLoadingAnalysis(false);
    }
  }, [basis, hasTrendSelection, trendStartPeriodId, trendEndPeriodId, trendMaxPeriods, t]);

  useEffect(() => {
    if (!hasSelection || !hasTrendSelection) {
      setStandardData(null);
      setTrendData(null);
      setSectorData(null);
      return;
    }
    loadAnalysis(selection.companyId, selection.periodId);
  }, [hasSelection, hasTrendSelection, selection.companyId, selection.periodId, trendStartPeriodId, trendEndPeriodId, trendMaxPeriods, loadAnalysis]);

  useEffect(() => {
    const loadSaved = async () => {
      if (!hasSelection) {
        setSavedReports([]);
        setSelectedSavedId('');
        return;
      }
      setLoadingSaved(true);
      try {
        setSavedReports(await fetchSavedAiFinancialReports(selection.companyId, selection.periodId));
      } catch {
        setSavedReports([]);
      } finally {
        setLoadingSaved(false);
      }
    };
    loadSaved();
  }, [hasSelection, selection.companyId, selection.periodId]);

  const issueRows = useMemo(() => {
    return (standardData?.items || []).map((item) => {
      const value = Number(item.calculatedValue);
      const standard = Number(item.standardValue);
      const missing = item.calculatedValue === null || item.calculatedValue === undefined;
      if (missing) {
        return { ...item, issueType: 'data_quality' };
      }
      const good = item.higherIsBetter === false ? value <= standard : value >= standard;
      if (good) {
        return { ...item, issueType: 'positive' };
      }
      return { ...item, issueType: riskKeys.has(item.key) ? 'risk' : 'performance' };
    });
  }, [standardData]);

  const healthScore = useMemo(() => {
    const positive = issueRows.filter((row) => row.issueType === 'positive').length;
    const critical = issueRows.filter((row) => {
      if (row.issueType !== 'risk' && row.issueType !== 'performance') {
        return false;
      }
      const dev = Math.abs(deviationPct(row.calculatedValue, row.standardValue) || 0);
      return dev >= 25;
    }).length;
    const missing = issueRows.filter((row) => row.issueType === 'data_quality').length;
    return clamp(42 + (positive * 7) - (critical * 12) - (missing * 6), 0, 100);
  }, [issueRows]);

  const trendHealth = useMemo(() => {
    if (!trendData?.items?.length) {
      return [];
    }
    return trendData.items.map((row) => {
      const score = (standardData?.items || []).reduce((acc, item) => {
        const value = Number(row.metrics?.[item.key]);
        const standard = Number(item.standardValue);
        if (Number.isNaN(value) || Number.isNaN(standard)) {
          return acc;
        }
        const good = item.higherIsBetter === false ? value <= standard : value >= standard;
        return acc + (good ? 7 : -3);
      }, 42);
      return { periodCode: row.periodCode, score: clamp(score, 0, 100) };
    });
  }, [trendData, standardData]);

  const prevScore = trendHealth.length > 1 ? trendHealth[trendHealth.length - 2].score : healthScore;
  const scoreDelta = healthScore - prevScore;
  const verdict = healthScore >= 70 ? t('strong') : healthScore >= 45 ? (language.startsWith('en') ? 'Balanced' : 'Dengeli') : (language.startsWith('en') ? 'Risky' : 'Riskli');
  const scoreBand = healthScore >= 70 ? 'ok' : healthScore >= 45 ? 'warn' : 'risk';

  const weakRows = issueRows.filter((row) => row.issueType === 'risk' || row.issueType === 'performance');
  const positiveRows = issueRows.filter((row) => row.issueType === 'positive');
  const dataQualityRows = issueRows.filter((row) => row.issueType === 'data_quality');

  const trendItem = (standardData?.items || []).find((item) => item.key === trendKey) || standardData?.items?.[0];
  const trendPoints = useMemo(() => {
    if (!trendData?.items?.length || !trendItem) {
      return [];
    }
    return trendData.items
      .map((row) => {
        const value = Number(row.metrics?.[trendItem.key]);
        if (Number.isNaN(value)) {
          return null;
        }
        return {
          periodCode: row.periodCode,
          value,
          diffPct: deviationPct(value, trendItem.standardValue)
        };
      })
      .filter(Boolean);
  }, [trendData, trendItem]);

  const topDeviationRows = useMemo(() => {
    const all = issueRows
      .filter((row) => row.calculatedValue !== null && row.standardValue !== null)
      .map((row) => {
        const pct = deviationPct(row.calculatedValue, row.standardValue) || 0;
        return { ...row, pct, capped: clamp(pct, -250, 250) };
      });
    const bad = all.filter((row) => row.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 2);
    const good = all.filter((row) => row.pct >= 0).sort((a, b) => b.pct - a.pct).slice(0, 3);
    return [...bad, ...good];
  }, [issueRows]);

  const impactRows = useMemo(() => {
    const rows = issueRows
      .filter((row) => row.calculatedValue !== null && row.standardValue !== null)
      .map((row) => ({ ...row, pct: deviationPct(row.calculatedValue, row.standardValue) || 0 }));
    if (sortMode === 'risk') {
      return rows.sort((a, b) => {
        const aw = a.issueType === 'risk' ? 2 : a.issueType === 'performance' ? 1 : 0;
        const bw = b.issueType === 'risk' ? 2 : b.issueType === 'performance' ? 1 : 0;
        return bw - aw || Math.abs(b.pct) - Math.abs(a.pct);
      });
    }
    if (sortMode === 'positive') {
      return rows.sort((a, b) => b.pct - a.pct);
    }
    return rows.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  }, [issueRows, sortMode]);

  const sectorRows = useMemo(() => {
    const stdMap = new Map((standardData?.items || []).map((item) => [item.key, Number(item.standardValue || 0)]));
    return (sectorData?.items || []).slice(0, 8).map((item) => {
      const company = Number(item.calculatedValue || 0);
      const sector = Number(item.sectorValue || 0);
      const standard = stdMap.get(item.key) || 0;
      const max = Math.max(company, sector, standard, 0.0001) * 1.2;
      return { ...item, company, sector, standard, max };
    });
  }, [sectorData, standardData]);

  const debtItem = (standardData?.items || []).find((item) => item.key === 'debt_to_equity_ratio');
  const debtCurrent = Number(debtItem?.calculatedValue);
  const debtStandard = Number(debtItem?.standardValue || 1.5);
  const debtValid = !Number.isNaN(debtCurrent);
  const riskLevel = !debtValid ? 'unknown' : debtCurrent <= debtStandard ? 'low' : debtCurrent <= debtStandard * 1.35 ? 'medium' : 'high';
  const simLevel = debtScenario <= debtStandard ? 'low' : debtScenario <= debtStandard * 1.35 ? 'medium' : 'high';
  const simGain = debtValid ? Math.max(0, (debtCurrent - debtScenario) * 6) : 0;

  const summaryCards = useMemo(() => {
    const best = [...positiveRows].sort((a, b) => Math.abs(deviationPct(b.calculatedValue, b.standardValue) || 0) - Math.abs(deviationPct(a.calculatedValue, a.standardValue) || 0))[0];
    const worst = [...weakRows].sort((a, b) => (deviationPct(a.calculatedValue, a.standardValue) || 0) - (deviationPct(b.calculatedValue, b.standardValue) || 0))[0];
    return [
      best ? `${t('reasonStrong')}: ${translateRatioTitle(best)} (${formatSigned(deviationPct(best.calculatedValue, best.standardValue), language, 1)}%).` : t('summaryStrongNoData'),
      worst ? `${t('reasonRisk')}: ${translateRatioTitle(worst)} (${formatSigned(deviationPct(worst.calculatedValue, worst.standardValue), language, 1)}%).` : t('summaryNoRisk'),
      worst ? `${t('topAction')}: ${riskKeys.has(worst.key) ? t('debtReductionPlan') : t('operationalPlan')}.` : t('loadDataFirst')
    ];
  }, [positiveRows, weakRows, t, translateRatioTitle, language]);

  const usageItems = useMemo(() => {
    if (!report?.usage) {
      return [];
    }
    return [
      { label: t('promptTokens'), value: report.usage.promptTokens ?? 0 },
      { label: t('completionTokens'), value: report.usage.completionTokens ?? 0 },
      { label: t('totalTokens'), value: report.usage.totalTokens ?? 0 },
      { label: t('totalCostUsd'), value: `$${formatUsd(report.usage.totalCostUsd)}` },
      { label: t('inputCostUsd'), value: `$${formatUsd(report.usage.inputCostUsd)}` },
      { label: t('outputCostUsd'), value: `$${formatUsd(report.usage.outputCostUsd)}` }
    ];
  }, [report, t]);

  const kpiCards = [
    {
      key: 'score', label: t('score'), value: formatNum(healthScore, language, 0), delta: formatSigned(scoreDelta, language, 1), note: `${t('aiVerdict')}: ${verdict}`, status: scoreBand,
      spark: trendHealth.map((row) => row.score)
    },
    {
      key: 'above', label: t('aboveStandard'), value: String(standardData?.aboveCount || 0), delta: formatSigned((standardData?.aboveCount || 0) - (standardData?.belowCount || 0), language, 0), note: formatPercent(((standardData?.aboveCount || 0) / Math.max(1, (standardData?.items || []).length)) * 100, language, 0), status: (standardData?.aboveCount || 0) >= (standardData?.belowCount || 0) ? 'ok' : 'warn',
      spark: trendHealth.map((row) => row.score)
    },
    {
      key: 'alarm', label: t('alarmRatio'), value: String(weakRows.length), delta: formatSigned(-weakRows.length, language, 0), note: `${riskLevel.toUpperCase()} ${t('leverage')}`, status: weakRows.length > 3 ? 'risk' : weakRows.length > 0 ? 'warn' : 'ok',
      spark: topDeviationRows.map((row) => 100 - Math.abs(row.capped))
    },
    {
      key: 'sector', label: t('aboveSector'), value: String(sectorData?.aboveCount || 0), delta: formatSigned((sectorData?.aboveCount || 0) - (sectorData?.belowCount || 0), language, 0), note: `${sectorData?.noBenchmarkCount || 0} ${t('noBenchmark')}`, status: (sectorData?.aboveCount || 0) >= (sectorData?.belowCount || 0) ? 'ok' : 'risk',
      spark: sectorRows.map((row) => (row.company / row.max) * 100)
    }
  ];

  const handleGenerate = async () => {
    if (!hasSelection) {
      setMessage(t('selectCompanyPeriod'));
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await generateAiFinancialReport(selection.companyId, selection.periodId, basis, language, temperature, trendStartPeriodId, trendEndPeriodId, trendMaxPeriods, provider || null);
      setReport(response);
      setAnalysisCoverage(response.coverage?.items || []);
      setAnalysisWarnings(response.coverage?.warnings || []);
      setSelectedSavedId(String(response.savedReportId || ''));
      setMessage(response.savedReportName ? `${t('reportSaved')} ${response.savedReportName}` : '');
      setSavedReports(await fetchSavedAiFinancialReports(selection.companyId, selection.periodId));
      await loadAnalysis(selection.companyId, selection.periodId);
    } catch (error) {
      setReport(null);
      setMessage(error instanceof Error ? error.message : t('reportGenerateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSaved = async (reportId) => {
    if (!reportId || !selection.companyId) {
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const saved = await fetchSavedAiFinancialReport(selection.companyId, reportId);
      setSelectedSavedId(String(reportId));
      setReport({
        reportText: saved.reportText,
        usage: saved.usage,
        latencyMs: saved.latencyMs,
        modelName: saved.modelName,
        source: saved.source,
        coverage: saved.coverage,
        savedReportId: saved.id,
        savedReportName: saved.reportName
      });
      setAnalysisCoverage(saved.coverage?.items || []);
      setAnalysisWarnings(saved.coverage?.warnings || []);
      setMessage(`${t('reportLoaded')} ${saved.reportName}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('reportLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = useCallback(async () => {
    if (!pageCaptureRef.current || exportingPdf) {
      return;
    }

    try {
      setExportingPdf(true);
      await exportAiReportPdf({
        element: pageCaptureRef.current,
        companyName: selection.company?.name || selection.company?.title || '',
        periodLabel: selection.period?.label || selection.period?.description || selection.period?.code || '',
        language
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('analysisLoadError'));
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, language, selection.company, selection.period, t]);

  const handleExportExcel = useCallback(async () => {
    if (!report || exportingExcel) {
      return;
    }

    try {
      setExportingExcel(true);
      await exportAiReportExcel({
        report,
        companyName: selection.company?.name || selection.company?.title || '',
        periodLabel: selection.period?.label || selection.period?.description || selection.period?.code || '',
        language
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('analysisLoadError'));
    } finally {
      setExportingExcel(false);
    }
  }, [exportingExcel, language, report, selection.company, selection.period, t]);

  return (
    <div className="card ai-financial-report-card" ref={pageCaptureRef}>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <div className="ai-header-actions" data-export-hidden="true">
            <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={!hasSelection || loading || loadingAnalysis || exportingPdf}>
              {exportingPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
            <button type="button" className="btn-secondary" onClick={handleExportExcel} disabled={!report || loading || loadingAnalysis || exportingExcel}>
              {exportingExcel ? t('exportingExcel') : t('exportExcel')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => loadAnalysis(selection.companyId, selection.periodId)} disabled={!hasSelection || loadingAnalysis || exportingPdf || exportingExcel}>
              {loadingAnalysis ? t('analysisLoading') : t('refreshAnalysis')}
            </button>
            <button type="button" className="btn-primary" disabled={!hasSelection || loading || exportingPdf || exportingExcel} onClick={handleGenerate}>
              {loading ? t('generating') : t('createAiReport')}
            </button>
          </div>
        }
      />

      <CompanyPeriodSelector
        onSelectionChange={setSelection}
        tenantAdminUserId={isSysAdmin ? selectedTenantAdminUserId : ''}
        locale={language}
      />

      <section className="ai-financial-report-controls">
        <label htmlFor="ai-basis-select">{t('reportingBasis')}
          <select id="ai-basis-select" value={basis} onChange={(event) => setBasis(event.target.value)}>
            <option value="IFRS">{t('ifrsDefault')}</option>
            <option value="VUK">{t('vukOption')}</option>
          </select>
        </label>
        <label htmlFor="ai-tenant-admin-select">{t('tenantUser')}
          <select
            id="ai-tenant-admin-select"
            value={selectedTenantAdminUserId}
            onChange={(event) => setSelectedTenantAdminUserId(event.target.value)}
            disabled={loadingTenantAdmins || !isSysAdmin}
          >
            {tenantAdminOptions.length === 0 ? (
              <option value="">{loadingTenantAdmins ? t('loading') : t('tenantAdminMissing')}</option>
            ) : (
              <>
                {isSysAdmin && <option value="">{t('allTenantAdmins')}</option>}
                {tenantAdminOptions.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </>
            )}
          </select>
        </label>
        <label htmlFor="ai-trend-start-period-select">{t('trendStartPeriod')}
          <select
            id="ai-trend-start-period-select"
            value={trendStartPeriodId}
            onChange={(event) => setTrendStartPeriodId(event.target.value)}
          >
            {filteredTrendStartPeriods.map((period) => (
              <option key={period.id} value={period.id}>{getPeriodLabel(period, language)}</option>
            ))}
          </select>
        </label>
        <label htmlFor="ai-trend-end-period-select">{t('trendEndPeriod')}
          <select
            id="ai-trend-end-period-select"
            value={trendEndPeriodId}
            onChange={(event) => setTrendEndPeriodId(event.target.value)}
          >
            {filteredTrendEndPeriods.map((period) => (
              <option key={period.id} value={period.id}>{getPeriodLabel(period, language)}</option>
            ))}
          </select>
        </label>
        <label htmlFor="ai-trend-max-periods-input">{t('trendMaxPeriod')}
          <input
            id="ai-trend-max-periods-input"
            type="number"
            min="1"
            max="60"
            value={trendMaxPeriods}
            onChange={(event) => setTrendMaxPeriods(Number(event.target.value) || 12)}
          />
        </label>
        <label htmlFor="ai-provider-select">{t('aiProvider')}
          <select id="ai-provider-select" value={provider} onChange={(event) => setProvider(event.target.value)}>
            <option value="">{t('providerAuto')}</option>
            {providerOptions.map((p) => (
              <option key={p.providerName} value={p.providerName} disabled={!p.configured}>
                {formatProviderName(p.providerName)}
                {!p.configured ? ` ${t('providerNotConfigured')}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="ai-temperature-input">{t('temperature')}
          <input id="ai-temperature-input" type="number" min="0" max="1" step="0.1" value={temperature} onChange={(event) => setTemperature(Number(event.target.value) || 0)} />
        </label>
      </section>

      {message && <p className="ai-financial-report-message">{message}</p>}
      {!message && <p className="ai-financial-report-message">{t('activeBasis')}: {basis}</p>}
      {!message && basis === 'IFRS' && <p className="ai-financial-report-message">{t('basisMismatchNotice')}</p>}
      {analysisCoverage.length > 0 && <section className="ai-financial-report-meta">{analysisCoverage.map((item) => <article key={item.key}><strong>{item.key}</strong><span>{item.status}</span></article>)}</section>}
      {analysisWarnings.length > 0 && <section className="analysis-section"><h3>{t('warnings')}</h3><div className="data-quality-list">{analysisWarnings.map((item) => <article key={item}><p>{item}</p></article>)}</div></section>}

      <section className="decision-kpi-grid">
        {kpiCards.map((item) => (
          <article key={item.key} className={`decision-kpi-card status-${item.status}`}>
            <div className="kpi-head"><span>{item.label}</span><strong>{item.value}</strong></div>
            <div className="kpi-meta"><span>{item.delta}</span><span>{item.note}</span></div>
            <Sparkline values={item.spark} />
          </article>
        ))}
      </section>

      <section className="ai-snapshot-row">
        <article className="snapshot-card gauge-card">
          <h3>{t('overallScore')}</h3>
          <div className="score-gauge" style={{ '--value': `${healthScore}` }}>
            <div className={`score-gauge-inner ${scoreBand}`}><strong>{Math.round(healthScore)}</strong><span>/100</span></div>
          </div>
          <p className="snapshot-verdict">{t('aiVerdict')}: <strong>{verdict}</strong></p>
        </article>

        <article className="snapshot-card executive-summary-card">
          <h3>{t('executiveSummary')}</h3>
          <div className="callout-row"><div className="callout-pill good">{t('strong')}</div><p>{summaryCards[0]}</p></div>
          <div className="callout-row"><div className="callout-pill risk">{t('risk')}</div><p>{summaryCards[1]}</p></div>
          <div className="callout-row"><div className="callout-pill action">{t('action')}</div><p>{summaryCards[2]}</p></div>
        </article>
        <article className="snapshot-card risk-alarm-card">
          <h3>{t('riskAlarm')}</h3>
          <div className={`risk-level risk-${riskLevel}`}><span>{riskLevel === 'high' ? t('high') : riskLevel === 'medium' ? t('med') : riskLevel === 'low' ? t('low') : t('unknown')}</span></div>
          <p>{t('debtToEquity')}: <strong>{formatNum(debtCurrent, language, 4)}</strong> vs {t('standard')} <strong>{formatNum(debtStandard, language, 4)}</strong></p>
          <label className="simulator" htmlFor="debt-simulator">{t('whatIf')}
            <input id="debt-simulator" type="range" min={debtStandard * 0.6} max={Math.max(debtCurrent || debtStandard, debtStandard) * 1.2} step="0.01" value={debtScenario || debtStandard} onChange={(event) => setDebtScenario(Number(event.target.value))} disabled={!debtValid} />
          </label>
          <div className="simulator-output">
            <span>{t('simulatedValue')}: {formatNum(debtScenario, language, 2)}</span>
            <span>{t('scoreImpact')}: +{formatNum(simGain, language, 1)}</span>
            <span>{t('newRisk')}: {simLevel.toUpperCase()}</span>
          </div>
        </article>
      </section>

      <section className="analysis-section">
        <div className="section-head"><h3>{t('ratioTrend')}</h3>
          <div className="inline-controls"><label htmlFor="trend-key">{t('ratio')}</label>
            <select id="trend-key" value={trendItem?.key || ''} onChange={(event) => setTrendKey(event.target.value)}>
              {(standardData?.items || []).map((item) => <option key={item.key} value={item.key}>{translateRatioTitle(item)}</option>)}
            </select>
          </div>
        </div>
        <TrendChart points={trendPoints} standard={trendItem?.standardValue} t={t} language={language} />
        <div className="trend-details-grid">
          {trendPoints.map((item) => (
            <article key={item.periodCode}>
              <span>{item.periodCode}</span>
              <strong>{formatNum(item.value, language, 4)}</strong>
              <small>{t('std')}: {formatNum(trendItem?.standardValue, language, 4)} | {t('diff')}: {formatPercent(item.diffPct, language, 2)}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="analysis-section">
        <div className="section-head"><h3>{t('sectorBenchmark')}</h3><button type="button" className="btn-secondary" onClick={() => navigate('/analysis/sector-ratio')}>{t('drillDown')}</button></div>
        <div className="bullet-list">
          {!sectorRows.length ? <p className="chart-empty">{t('noSectorData')}</p> : sectorRows.map((item) => (
            <article key={item.key} className="bullet-row">
              <div className="bullet-title"><strong>{translateRatioTitle(item)}</strong><span>{t('company')} {formatNum(item.company, language, 4)} | {t('sector')} {formatNum(item.sector, language, 4)} | {t('std')} {formatNum(item.standard, language, 4)}</span></div>
              <div className="bullet-track"><div className="track-zones" /><div className="company-fill" style={{ width: `${clamp((item.company / item.max) * 100, 0, 100)}%` }} /><div className="sector-marker" style={{ left: `${clamp((item.sector / item.max) * 100, 0, 100)}%` }} /><div className="standard-marker" style={{ left: `${clamp((item.standard / item.max) * 100, 0, 100)}%` }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="analysis-section">
        <div className="section-head"><h3>{t('criticalDeviation')}</h3><span className="caps-note">{t('cap')}</span></div>
        <div className="diverging-list">
          {!topDeviationRows.length ? <p className="chart-empty">{t('noDeviationData')}</p> : topDeviationRows.map((item) => (
            <article key={item.key} className="diverging-row">
              <div className="diverging-label"><strong>{translateRatioTitle(item)}</strong><span>{formatSigned(Number(item.calculatedValue) - Number(item.standardValue), language, 4)} | {formatSigned(item.pct, language, 1)}%</span></div>
              <div className="diverging-track"><span className="center-line" /><div className={`diverging-bar ${item.capped >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(item.capped) / 5)}%`, [item.capped >= 0 ? 'left' : 'right']: '50%' }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="analysis-section">
        <div className="section-head"><h3>{t('strengthWeakness')}</h3><button type="button" className="btn-secondary" onClick={() => navigate('/analysis/ratios-standard')}>{t('drillDown')}</button></div>
        <div className="driver-columns">
          <div className="driver-column"><h4>{t('strengths')}</h4>{positiveRows.slice(0, 7).map((item) => {
            const company = Number(item.calculatedValue || 0);
            const standard = Number(item.standardValue || 0);
            const max = Math.max(company, standard, 0.0001) * 1.2;
            return <article key={item.key} className="driver-card"><header><strong>{translateRatioTitle(item)}</strong><span className="trace" title={`${t('formulaHint')}: ${item.formula || fallbackFormula[language]?.[item.key] || t('undefined')}`}>i</span></header><p>{t('company')} {formatNum(company, language, 4)} | {t('std')} {formatNum(standard, language, 4)} | {t('diff')} {formatSigned(company - standard, language, 4)} ({formatSigned(deviationPct(company, standard), language, 1)}%)</p><div className="mini-compare"><span className="mini-fill" style={{ width: `${clamp((company / max) * 100, 0, 100)}%` }} /><span className="mini-target" style={{ left: `${clamp((standard / max) * 100, 0, 100)}%` }} /></div></article>;
          })}</div>
          <div className="driver-column"><h4>{t('weaknesses')}</h4>{weakRows.slice(0, 7).map((item) => {
            const company = Number(item.calculatedValue || 0);
            const standard = Number(item.standardValue || 0);
            const max = Math.max(company, standard, 0.0001) * 1.2;
            return <article key={item.key} className="driver-card weak"><header><strong>{translateRatioTitle(item)}</strong><span className="trace" title={`${t('formulaHint')}: ${item.formula || fallbackFormula[language]?.[item.key] || t('undefined')}`}>i</span></header><p>{t('company')} {formatNum(company, language, 4)} | {t('std')} {formatNum(standard, language, 4)} | {t('diff')} {formatSigned(company - standard, language, 4)} ({formatSigned(deviationPct(company, standard), language, 1)}%)</p><div className="mini-compare"><span className="mini-fill" style={{ width: `${clamp((company / max) * 100, 0, 100)}%` }} /><span className="mini-target" style={{ left: `${clamp((standard / max) * 100, 0, 100)}%` }} /></div></article>;
          })}</div>
        </div>
      </section>

      <section className="analysis-section">
        <div className="section-head"><h3>{t('highestImpactRatios')}</h3><div className="inline-controls"><label htmlFor="impact-sort">{t('sort')}</label><select id="impact-sort" value={sortMode} onChange={(event) => setSortMode(event.target.value)}><option value="abs">{t('sortAbs')}</option><option value="risk">{t('sortRisk')}</option><option value="positive">{t('sortPositive')}</option></select></div></div>
        <div className="impact-table-wrap">
          <table className="impact-table"><thead><tr><th>{t('ratioCol')}</th><th>{t('company')}</th><th>{t('standard')}</th><th>{t('difference')}</th><th>{t('status')}</th></tr></thead><tbody>
            {!impactRows.length ? <tr><td colSpan={5} className="chart-empty">{t('noRatioData')}</td></tr> : impactRows.slice(0, 10).map((item) => (
              <tr key={item.key}><td>{translateRatioTitle(item)}</td><td>{formatNum(item.calculatedValue, language, 4)}</td><td>{formatNum(item.standardValue, language, 4)}</td><td><div className="table-diverging"><span className="table-center" /><div className={`table-diverging-bar ${item.pct >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(item.pct) / 5)}%`, [item.pct >= 0 ? 'left' : 'right']: '50%' }} /><strong>{formatSigned(item.pct, language, 1)}%</strong></div></td><td><span className={`issue-pill issue-${item.issueType}`}>{item.issueType === 'data_quality' ? t('dataQuality') : item.issueType === 'risk' ? t('risk') : item.issueType === 'performance' ? t('performance') : t('positive')}</span></td></tr>
            ))}
          </tbody></table>
        </div>
      </section>

      <section className="analysis-section action-plan-section">
        <h3>{t('actionCards')}</h3>
        <div className="action-grid">
          {weakRows.slice(0, 3).map((item, index) => (
            <article key={item.key} className="action-card"><header><h4>{riskKeys.has(item.key) ? t('debtProgram') : `${translateRatioTitle(item)} ${t('package')}`}</h4><span>{index === 0 ? 'P1' : index === 1 ? 'P2' : 'P3'}</span></header><p>{t('impactArea')}: <strong>{riskKeys.has(item.key) ? t('risk') : t('efficiency')}</strong></p><p>{t('expectedImpact')}: <strong>{t('score')} +{Math.max(2, Math.round(Math.abs(deviationPct(item.calculatedValue, item.standardValue) || 0) / 12))}</strong></p><ul><li>{t('cashFlowProj')}</li><li>{t('monthlyKpi')}</li><li>{t('ownerDueDate')}</li></ul><button type="button" className="btn-secondary">{t('addToPlan')}</button></article>
          ))}
        </div>
      </section>

      <section className="analysis-section data-quality-section">
        <h3>{t('dataQualityIssues')}</h3>
        {!dataQualityRows.length ? <p className="chart-empty">{t('dataQualityNotFound')}</p> : <div className="data-quality-list">{dataQualityRows.map((item) => <article key={item.key}><strong>{translateRatioTitle(item)}</strong><p>{t('issueType')}: {t('dataQualityIssue')}</p><p>{t('note')}: {item.note || t('invalidValue')}</p></article>)}</div>}
      </section>

      <section className="ai-saved-reports-card">
        <div className="ai-saved-reports-header"><h3>{t('savedReports')}</h3><span>{loadingSaved ? t('loading') : `${savedReports.length} ${t('recordCount')}`}</span></div>
        <div className="ai-saved-reports-table-wrapper">
          <table className="ai-saved-reports-table"><thead><tr><th>{t('reportName')}</th><th>{t('dateTime')}</th><th></th></tr></thead><tbody>
            {savedReports.length === 0 ? <tr><td colSpan={3} className="ai-saved-empty">{t('noSavedReport')}</td></tr> : savedReports.map((item) => (
              <tr key={item.id} className={String(item.id) === selectedSavedId ? 'is-selected' : ''}><td>{item.reportName}</td><td>{formatDateTime(item.createdAt, language)}</td><td><button type="button" className="btn-secondary" onClick={() => handleLoadSaved(item.id)} disabled={loading}>{t('load')}</button></td></tr>
            ))}
          </tbody></table>
        </div>
      </section>

      {report && <><section className="ai-financial-report-meta"><article><strong>{t('savedName')}</strong><span>{report.savedReportName || '-'}</span></article><article><strong>Model</strong><span>{report.modelName || '-'}</span></article><article><strong>{t('source')}</strong><span>{formatProviderName(report.source)}</span></article><article><strong>{t('latency')}</strong><span>{report.latencyMs ?? 0} ms</span></article></section><section className="ai-financial-report-usage">{usageItems.map((item) => <article key={item.label}><span>{item.label}</span><strong>{item.value}</strong></article>)}</section><section className="ai-financial-report-body"><h3>{t('aiReportText')}</h3><AiReportMarkdown text={report.reportText || ''} /></section></>}
    </div>
  );
}

export default AiFinancialReportPage;
